var __assign = (this && this.__assign) || function () {
    __assign = Object.assign || function(t) {
        for (var s, i = 1, n = arguments.length; i < n; i++) {
            s = arguments[i];
            for (var p in s) if (Object.prototype.hasOwnProperty.call(s, p))
                t[p] = s[p];
        }
        return t;
    };
    return __assign.apply(this, arguments);
};
import { jsx as _jsx, jsxs as _jsxs, Fragment as _Fragment } from "react/jsx-runtime";
import React, { useState, useEffect } from 'react';
import * as Sentry from '@sentry/react';
import { TbSpeakerphone } from 'react-icons/tb';
import { Badge, Modal, Button, Empty } from 'antd';
import { errorStore } from '../utils/ErrorStore';
var SentryFeedback = function (_a) {
    var _b = _a.className, className = _b === void 0 ? '' : _b, _c = _a.showText, showText = _c === void 0 ? false : _c;
    var _d = useState(0), errorCount = _d[0], setErrorCount = _d[1];
    var _e = useState([]), errors = _e[0], setErrors = _e[1];
    var _f = useState(false), isModalVisible = _f[0], setIsModalVisible = _f[1];
    useEffect(function () {
        // Initial load
        var count = errorStore.getErrorCount();
        var allErrors = errorStore.getErrors();
        setErrorCount(count);
        setErrors(allErrors);
        // Subscribe to changes
        var unsubscribe = errorStore.subscribe(function () {
            var newCount = errorStore.getErrorCount();
            var newErrors = errorStore.getErrors();
            setErrorCount(newCount);
            setErrors(newErrors);
        });
        return function () {
            unsubscribe();
        };
    }, []);
    var handleIconClick = function () {
        if (errorCount > 0) {
            setIsModalVisible(true);
        }
        else {
            handleReportIssue();
        }
    };
    var handleReportIssue = function () {
        try {
            var eventId = Sentry.captureMessage('User Feedback Request', {
                level: 'info',
                tags: {
                    reportType: 'manual_user_report',
                    source: 'feedback_button'
                },
                extra: {
                    timestamp: new Date().toISOString(),
                    userEmail: localStorage.getItem('loggedInEmail'),
                    userName: localStorage.getItem('loggedInName'),
                    currentUrl: window.location.href,
                    userAgent: navigator.userAgent
                }
            });
            var userEmail = localStorage.getItem('loggedInEmail') || '';
            var userName = localStorage.getItem('loggedInName') || '';
            // Show dialog
            Sentry.showReportDialog({
                eventId: eventId,
                title: 'Report an Issue',
                subtitle: 'Please describe what went wrong or what you expected to happen.',
                subtitle2: 'Our team will be notified and investigate immediately.',
                labelName: 'Name',
                labelEmail: 'Email',
                labelComments: 'What happened?',
                labelClose: 'Close',
                labelSubmit: 'Submit Report',
                errorGeneric: 'An error occurred. Please try again.',
                errorFormEntry: 'Please fill in all required fields.',
                successMessage: 'Thank you! Your feedback has been received.',
                user: {
                    email: userEmail,
                    name: userName,
                }
            });
        }
        catch (error) {
            console.error('❌ Error showing Sentry dialog:', error);
            alert('Failed to open feedback form. Please try again.');
        }
    };
    var handleReportError = function (error) {
        try {
            var errorToReport = new Error("[USER REPORTED] " + error.message);
            if (error.stack) {
                errorToReport.stack = error.stack;
            }
            else if (error.details && typeof error.details === 'object' && 'stack' in error.details) {
                errorToReport.stack = error.details.stack;
            }
            // Get user info
            var userEmail = localStorage.getItem('loggedInEmail') || '';
            var userName = localStorage.getItem('loggedInName') || '';
            var eventId = Sentry.captureException(errorToReport, {
                level: 'error',
                tags: {
                    errorType: 'user_reported',
                    severity: error.severity,
                    manualReport: 'true',
                    reportSource: 'error_list',
                    errorCategory: 'application_error',
                    userReported: 'yes'
                },
                extra: {
                    errorMessage: error.message,
                    originalTimestamp: error.timestamp,
                    userReportedAt: new Date().toISOString(),
                    originalDetails: error.details,
                    errorId: error.id,
                    currentUrl: window.location.href,
                    userEmail: userEmail,
                    userName: userName,
                    reportNote: 'User manually reported this error from error list',
                    browserInfo: navigator.userAgent,
                    screenSize: window.innerWidth + "x" + window.innerHeight,
                    facilityId: localStorage.getItem('facilityId'),
                    organizationId: localStorage.getItem('organizationId')
                },
                user: {
                    email: userEmail,
                    username: userName,
                }
            });
            if (!eventId) {
                throw new Error('Failed to capture event - no event ID returned');
            }
            // Mark as reported
            errorStore.markAsReported(error.id);
            Sentry.showReportDialog({
                eventId: eventId,
                title: 'Report Error to Team',
                subtitle: "Error: " + error.message,
                subtitle2: 'Please add any additional details that might help us fix this issue.',
                labelComments: 'Additional Information (optional)',
                labelClose: 'Close',
                labelSubmit: 'Submit Feedback',
                user: {
                    email: userEmail,
                    name: userName,
                }
            });
        }
        catch (err) {
            console.error('❌ Error reporting to Sentry:', err);
            alert('Failed to report error. Please try again.');
        }
    };
    var handleClearAll = function () {
        errorStore.clearAll();
        setIsModalVisible(false);
    };
    var getSeverityColor = function (severity) {
        switch (severity) {
            case 'high':
                return 'bg-red-500';
            case 'medium':
                return 'bg-yellow-500';
            case 'low':
                return 'bg-orange-500';
            default:
                return 'bg-gray-500';
        }
    };
    var getTimeAgo = function (timestamp) {
        var seconds = Math.floor((Date.now() - new Date(timestamp).getTime()) / 1000);
        if (seconds < 60)
            return 'Just now';
        if (seconds < 3600)
            return Math.floor(seconds / 60) + " mins ago";
        if (seconds < 86400)
            return Math.floor(seconds / 3600) + " hours ago";
        return new Date(timestamp).toLocaleString();
    };
    return (_jsxs(_Fragment, { children: [_jsx("div", __assign({ style: {
                    position: 'fixed',
                    top: window.innerWidth <= 768 ? 'auto' : '5px',
                    bottom: window.innerWidth <= 768 ? '10px' : 'auto',
                    right: window.innerWidth <= 768 ? '20px' : '10px',
                    zIndex: 1000
                } }, { children: _jsx(Badge, __assign({ count: errorCount, offset: [-5, 5], style: {
                        backgroundColor: '#ef4444',
                        boxShadow: '0 2px 8px rgba(239, 68, 68, 0.5)'
                    } }, { children: _jsx("button", __assign({ onClick: handleIconClick, className: "bg-white transition-all duration-300", style: {
                            border: '2.5px solid #d1d5db',
                            borderRadius: '50%',
                            width: '35px',
                            height: '35px',
                            display: 'flex',
                            alignItems: 'center',
                            justifyContent: 'center',
                            padding: 0,
                            cursor: 'pointer',
                            boxShadow: '0 2px 6px rgba(0, 0, 0, 0.1)'
                        }, onMouseEnter: function (e) {
                            e.currentTarget.style.transform = 'scale(1.08)';
                            e.currentTarget.style.boxShadow = '0 4px 12px rgba(0, 0, 0, 0.15)';
                        }, onMouseLeave: function (e) {
                            e.currentTarget.style.transform = 'scale(1)';
                            e.currentTarget.style.boxShadow = '0 2px 6px rgba(0, 0, 0, 0.1)';
                        }, "aria-label": "View application errors", title: "View application errors" }, { children: _jsx(TbSpeakerphone, { style: {
                                fontSize: '24px',
                                color: '#303030'
                            } }, void 0) }), void 0) }), void 0) }), void 0), _jsx(Modal, __assign({ title: _jsxs("div", __assign({ className: "flex items-center justify-between" }, { children: [_jsxs("span", { children: ["Application Errors (", errors.length, ")"] }, void 0), errors.length > 0 && (_jsxs("span", __assign({ className: "text-sm text-gray-500" }, { children: [errorCount, " unreported"] }), void 0))] }), void 0), visible: isModalVisible, onCancel: function () { return setIsModalVisible(false); }, footer: [
                    _jsx(Button, __assign({ onClick: handleClearAll, disabled: errors.length === 0 }, { children: "Clear All" }), "clear"),
                    _jsx(Button, __assign({ type: "primary", onClick: handleReportIssue }, { children: "Report New Issue" }), "report"),
                    _jsx(Button, __assign({ onClick: function () { return setIsModalVisible(false); } }, { children: "Close" }), "close")
                ], width: 700, bodyStyle: { maxHeight: '60vh', overflowY: 'auto' } }, { children: errors.length === 0 ? (_jsx(Empty, { image: Empty.PRESENTED_IMAGE_SIMPLE, description: "No errors recorded" }, void 0)) : (_jsx("div", __assign({ className: "space-y-3" }, { children: errors.map(function (error) { return (_jsx("div", __assign({ className: "p-4 border rounded-lg " + (error.reported ? 'bg-gray-50 opacity-60' : 'bg-white') }, { children: _jsxs("div", __assign({ className: "flex items-start justify-between gap-3" }, { children: [_jsxs("div", __assign({ className: "flex-1" }, { children: [_jsxs("div", __assign({ className: "flex items-center gap-2 mb-1" }, { children: [_jsx("span", { className: "w-2 h-2 rounded-full " + getSeverityColor(error.severity) }, void 0), _jsx("h4", __assign({ className: "font-semibold text-sm" }, { children: error.message }), void 0)] }), void 0), _jsx("p", __assign({ className: "text-xs text-gray-500" }, { children: getTimeAgo(error.timestamp) }), void 0), error.stack && (_jsxs("details", __assign({ className: "mt-2" }, { children: [_jsx("summary", __assign({ className: "text-xs text-blue-600 cursor-pointer" }, { children: "View stack trace" }), void 0), _jsx("pre", __assign({ className: "text-xs bg-gray-100 p-2 mt-1 rounded overflow-x-auto" }, { children: error.stack.split('\n').slice(0, 5).join('\n') }), void 0)] }), void 0))] }), void 0), _jsx(Button, __assign({ size: "small", type: error.reported ? 'default' : 'primary', onClick: function () { return handleReportError(error); }, disabled: error.reported }, { children: error.reported ? '✓ Reported' : 'Report' }), void 0)] }), void 0) }), error.id)); }) }), void 0)) }), void 0)] }, void 0));
};
export default SentryFeedback;
